import { elements, type Element, categoryLabels } from "@/data/elements";
import { DAILY_POOL, ELEMENT_FACTS, type ElementFact } from "./facts";

/**
 * Daily Element engine.
 *
 * Everything is deterministic from today's date so every visitor in the
 * world sees the same element + same questions on the same calendar day
 * (Wordle-style). We use the user's *local* date so a student in Tokyo
 * and a student in NYC each get a fresh puzzle when their day rolls
 * over - more forgiving than UTC for our school audience.
 *
 * Persistence is in localStorage only - no accounts, no backend.
 */

// ---------- date helpers ----------

/** YYYY-MM-DD in the user's local timezone. */
export function todayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Cheap, stable string hash (FNV-1a 32-bit). */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Mulberry32 PRNG seeded from a 32-bit integer. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(arr: T[], rand: () => number): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ---------- pick of the day ----------

export interface DailyMcq {
  id: string;
  prompt: string;
  options: string[];
  correctIndex: number;
  explanation?: string;
}

export interface DailyPuzzle {
  dateKey: string;
  element: Element;
  fact: ElementFact;
  questions: DailyMcq[];
}

function elementByNumber(n: number): Element {
  const el = elements.find((e) => e.number === n);
  if (!el) throw new Error(`No element with atomic number ${n}`);
  return el;
}

/** Pick distractors for an MCQ from a list of values, excluding the correct one. */
function pickDistractors(
  correct: string,
  pool: string[],
  rand: () => number,
  count = 3,
): string[] {
  const unique = Array.from(new Set(pool)).filter((v) => v && v !== correct);
  return shuffle(unique, rand).slice(0, count);
}

function buildQuestions(el: Element, rand: () => number): DailyMcq[] {
  const qs: DailyMcq[] = [];

  // Q1: Symbol
  {
    const correct = el.symbol;
    const distractors = pickDistractors(
      correct,
      elements.map((e) => e.symbol),
      rand,
    );
    const opts = shuffle([correct, ...distractors], rand);
    qs.push({
      id: "symbol",
      prompt: `What is the chemical symbol for ${el.name}?`,
      options: opts,
      correctIndex: opts.indexOf(correct),
    });
  }

  // Q2: Category
  {
    const correct = categoryLabels[el.category];
    const distractors = pickDistractors(
      correct,
      Object.values(categoryLabels),
      rand,
    );
    const opts = shuffle([correct, ...distractors], rand);
    qs.push({
      id: "category",
      prompt: `Which group does ${el.name} (${el.symbol}) belong to?`,
      options: opts,
      correctIndex: opts.indexOf(correct),
      explanation: `${el.name} is classified as a ${correct.toLowerCase()}.`,
    });
  }

  // Q3: Atomic number
  {
    const correct = String(el.number);
    // Distractors: nearby atomic numbers, plus a couple of random ones.
    const nearby = [el.number - 7, el.number - 3, el.number + 3, el.number + 8]
      .filter((n) => n >= 1 && n <= 118 && n !== el.number)
      .map(String);
    const distractors = pickDistractors(correct, nearby, rand);
    const opts = shuffle([correct, ...distractors], rand);
    qs.push({
      id: "atomic-number",
      prompt: `What is the atomic number of ${el.name}?`,
      options: opts,
      correctIndex: opts.indexOf(correct),
    });
  }

  return qs;
}

/** Build today's puzzle (pure, deterministic from `dateKey`). */
export function getDailyPuzzle(dateKey: string = todayKey()): DailyPuzzle {
  const seed = hashString(`elementpad-daily:${dateKey}`);
  const rand = mulberry32(seed);

  const idx = Math.floor(rand() * DAILY_POOL.length);
  const number = DAILY_POOL[idx];
  const element = elementByNumber(number);
  const fact = ELEMENT_FACTS[number];
  const questions = buildQuestions(element, rand);

  return { dateKey, element, fact, questions };
}

// ---------- persistence ----------

const RESULT_KEY_PREFIX = "elementpad:daily:result:";
const STREAK_KEY = "elementpad:daily:streak";
const LAST_PLAYED_KEY = "elementpad:daily:lastPlayed";

export interface DailyResult {
  dateKey: string;
  /** Index chosen for each question, in order. -1 means not answered. */
  answers: number[];
  /** Number correct out of total. */
  score: number;
  total: number;
  completedAt: string; // ISO timestamp
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function loadResult(dateKey: string): DailyResult | null {
  const ls = safeStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(RESULT_KEY_PREFIX + dateKey);
    return raw ? (JSON.parse(raw) as DailyResult) : null;
  } catch {
    return null;
  }
}

/**
 * Persist today's result and update the rolling streak. The streak only
 * advances when the user finishes the puzzle on the *next calendar day*
 * after their last play; gaps reset it to 1.
 */
export function saveResult(result: DailyResult): { current: number; best: number } {
  const ls = safeStorage();
  if (!ls) return { current: 0, best: 0 };
  try {
    ls.setItem(RESULT_KEY_PREFIX + result.dateKey, JSON.stringify(result));

    const lastPlayed = ls.getItem(LAST_PLAYED_KEY);
    const prev = ls.getItem(STREAK_KEY);
    const prevStreak = prev ? Math.max(0, parseInt(JSON.parse(prev).current ?? "0", 10) || 0) : 0;
    const prevBest = prev ? Math.max(0, parseInt(JSON.parse(prev).best ?? "0", 10) || 0) : 0;

    let nextStreak = 1;
    if (lastPlayed === result.dateKey) {
      // Already played today - keep current streak.
      nextStreak = prevStreak || 1;
    } else if (lastPlayed) {
      // Was yesterday?
      const last = new Date(lastPlayed + "T00:00:00");
      const today = new Date(result.dateKey + "T00:00:00");
      const diffDays = Math.round((today.getTime() - last.getTime()) / 86400000);
      nextStreak = diffDays === 1 ? prevStreak + 1 : 1;
    }

    const nextBest = Math.max(prevBest, nextStreak);
    ls.setItem(LAST_PLAYED_KEY, result.dateKey);
    ls.setItem(STREAK_KEY, JSON.stringify({ current: nextStreak, best: nextBest }));
    return { current: nextStreak, best: nextBest };
  } catch {
    return { current: 0, best: 0 };
  }
}

export function loadStreak(): { current: number; best: number } {
  const ls = safeStorage();
  if (!ls) return { current: 0, best: 0 };
  try {
    const raw = ls.getItem(STREAK_KEY);
    if (!raw) return { current: 0, best: 0 };
    const parsed = JSON.parse(raw);
    const current = parseInt(parsed.current ?? "0", 10) || 0;
    const best = parseInt(parsed.best ?? "0", 10) || 0;

    // Auto-expire current streak if last play was >1 day ago.
    const lastPlayed = ls.getItem(LAST_PLAYED_KEY);
    if (lastPlayed) {
      const last = new Date(lastPlayed + "T00:00:00");
      const today = new Date(todayKey() + "T00:00:00");
      const diffDays = Math.round((today.getTime() - last.getTime()) / 86400000);
      if (diffDays > 1) return { current: 0, best };
    }
    return { current, best };
  } catch {
    return { current: 0, best: 0 };
  }
}

// ---------- share text ----------

/** Build a Wordle-style share string with green/red squares per question. */
export function buildShareText(puzzle: DailyPuzzle, result: DailyResult): string {
  const grid = puzzle.questions
    .map((q, i) => (result.answers[i] === q.correctIndex ? "🟩" : "🟥"))
    .join("");
  return [
    `ElementPad Daily ${puzzle.dateKey}`,
    `${puzzle.element.symbol} - ${result.score}/${result.total}`,
    grid,
    "https://elementpad.net/daily",
  ].join("\n");
}